import React from 'react';
import { Twitch, Youtube, MessageCircle, Mail, Instagram } from 'lucide-react';
import { streamer } from '../mock';
import { useTwitchChannel } from '../hooks/useTwitch';
import { useBio } from '../hooks/useBio';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.94a8.16 8.16 0 0 0 4.77 1.52V7.02a4.85 4.85 0 0 1-1.84-.33Z" />
  </svg>
);

const KickIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M3 3h5v5h2V5h2V3h5v5h-2v2h-2v4h2v2h2v5h-5v-3h-2v-2H8v5H3V3Z" />
  </svg>
);

const SOCIALS = [
  {
    key: 'twitch',
    label: 'Twitch',
    href: streamer.socials.twitch,
    Icon: Twitch,
    hover: 'hover:bg-violet-600'
  },
  {
    key: 'kick',
    label: 'Kick',
    href: streamer.socials.kick,
    Icon: KickIcon,
    hover: 'hover:bg-green-600'
  },
  {
    key: 'youtube',
    label: 'YouTube',
    href: streamer.socials.youtube,
    Icon: Youtube,
    hover: 'hover:bg-red-600'
  },
  {
    key: 'discord',
    label: 'Discord',
    href: streamer.socials.discord,
    Icon: MessageCircle,
    hover: 'hover:bg-indigo-600'
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    href: streamer.socials.tiktok,
    Icon: TikTokIcon,
    hover: 'hover:bg-pink-600'
  },
  {
    key: 'instagram',
    label: 'Instagram',
    href: streamer.socials.instagram,
    Icon: Instagram,
    hover: 'hover:bg-fuchsia-600'
  },
  {
    key: 'email',
    label: 'Email',
    href: `mailto:${streamer.socials.email}`,
    Icon: Mail,
    hover: 'hover:bg-zinc-700'
  }
];

export default function Footer() {
  const { data: channel } = useTwitchChannel();
  const { bio } = useBio();
  const avatar = channel?.broadcaster?.profile_image_url || streamer.avatar;
  const displayName = channel?.broadcaster?.display_name || 'JorDievoorje';

  return (
    <footer className="relative border-t border-white/5 bg-zinc-950/85 backdrop-blur-sm">
      <div
        className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 grid md:grid-cols-3 gap-10"
        style={{ paddingBottom: 'calc(3rem + var(--follower-ticker-h, 0px))' }}
      >
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-600 p-[2px]">
              <img
                src={avatar}
                alt={displayName}
                className="w-full h-full rounded-full object-cover"
                data-testid="footer-avatar"
              />
            </div>
            <span className="font-bold text-white text-lg">{displayName}</span>
          </div>
          <p className="text-zinc-400 text-sm mt-4 max-w-xs">
            {bio.tagline}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Navigatie</span>
          <a href="#home" className="text-zinc-300 hover:text-violet-300 text-sm">Home</a>
          <a href="#schedule" className="text-zinc-300 hover:text-violet-300 text-sm">Stream schema</a>
          <a href="#vods" className="text-zinc-300 hover:text-violet-300 text-sm">Recente streams</a>
          <a href="#games" className="text-zinc-300 hover:text-violet-300 text-sm">Games</a>
          <a href="#contact-form" className="text-zinc-300 hover:text-violet-300 text-sm">Contact</a>
        </div>

        <div>
          <span className="text-xs uppercase tracking-wider text-zinc-500 mb-3 block">Volg mij</span>
          <div className="flex flex-wrap gap-2" data-testid="footer-socials">
            {SOCIALS.map(({ key, label, href, Icon, hover }) => (
              <a
                key={key}
                href={href}
                target={key === 'email' ? undefined : '_blank'}
                rel={key === 'email' ? undefined : 'noreferrer'}
                className={`w-10 h-10 rounded-lg bg-white/5 ${hover} border border-white/10 flex items-center justify-center text-white transition-colors`}
                aria-label={label}
                data-testid={`footer-social-${key}`}
              >
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-6">
            © {new Date().getFullYear()} {displayName}. Made with too much coffee.
          </p>
        </div>
      </div>
    </footer>
  );
}
